// ══════════════════════════════════════════════════════════════
// COMPASS – Process Selectors
// ══════════════════════════════════════════════════════════════
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ProcessState } from './process.state';
import { EntrustSignatureStatusResponseDto } from '../../api/models/entrust-signature-status-response-dto';

export const selectProcessState = createFeatureSelector<ProcessState>('process');

export const selectAllProcesses = createSelector(
  selectProcessState,
  (state) => state.processes
);

export const selectSelectedProcess = createSelector(
  selectProcessState,
  (state) => state.selectedProcess
);

export const selectIndividualForm = createSelector(
  selectProcessState,
  (state) => state.individualForm
);

export const selectCompanyForm = createSelector(
  selectProcessState,
  (state) => state.companyForm
);

export const selectProcessLoading = createSelector(
  selectProcessState,
  (state) => state.loading
);

/** The process list request only (the processes dashboard): not ended by loading the open process or its forms. */
export const selectProcessesLoading = createSelector(
  selectProcessState,
  (state) => state.processesLoading
);

export const selectFormSaving = createSelector(
  selectProcessState,
  (state) => state.formSaving
);

export const selectProcessError = createSelector(
  selectProcessState,
  (state) => state.error
);

export const selectChangeRequests = createSelector(
  selectProcessState,
  (state) => state?.changeRequests ?? []
);

export const selectChangeRequestsLoading = createSelector(
  selectProcessState,
  (state) => state?.changeRequestsLoading ?? false
);

export const selectStatusTransitioning = createSelector(
  selectProcessState,
  (state) => state.statusTransitioning
);

export const selectReopening = createSelector(
  selectProcessState,
  (state) => state.reopening
);

export const selectClientReopening = createSelector(
  selectProcessState,
  (state) => state.clientReopening
);

export const selectFormVersions = createSelector(
  selectProcessState,
  (state) => state.formVersions
);

export const selectFormVersionsLoading = createSelector(
  selectProcessState,
  (state) => state.formVersionsLoading
);

export const selectSelectedFormVersion = createSelector(
  selectProcessState,
  (state) => state.selectedFormVersion
);

export const selectFormVersionDetailLoading = createSelector(
  selectProcessState,
  (state) => state.formVersionDetailLoading
);

export const selectChangeRequestCounts = createSelector(
  selectChangeRequests,
  (changeRequests) => {
    if (!Array.isArray(changeRequests)) return { open: 0, resolved: 0, closed: 0 };
    return {
      open: changeRequests.filter((cr) => cr.status === 'OPEN').length,
      resolved: changeRequests.filter((cr) => cr.status === 'RESOLVED').length,
      closed: changeRequests.filter((cr) => cr.status === 'CLOSED').length,
    };
  }
);

// Approval Workflow
export const selectApprovalState = createSelector(
  selectProcessState,
  (state) => state.approvalState
);

export const selectApprovalsLoading = createSelector(
  selectProcessState,
  (state) => state.approvalsLoading
);

export const selectApprovalsError = createSelector(
  selectProcessState,
  (state) => state.approvalsError
);

export const selectApprovals = createSelector(
  selectProcessState,
  (state) => state.approvals
);

export const selectApprovalLoading = createSelector(
  selectProcessState,
  (state) => state.approvalLoading
);

export const selectApprovalDeciding = createSelector(
  selectProcessState,
  (state) => state.approvalDeciding
);

// Timeline
// Split into two selectors so the sort only re-runs when the raw
// timeline array reference changes (not when unrelated ProcessState
// fields like `loading` or `error` change). This preserves memoization
// for downstream consumers (AG Grid, signals) that receive the sorted array.
const selectRawTimeline = createSelector(
  selectProcessState,
  (state) => state.timeline
);

export const selectTimeline = createSelector(
  selectRawTimeline,
  (timeline) => timeline.slice().sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  })
);

export const selectTimelineLoading = createSelector(
  selectProcessState,
  (state) => state.timelineLoading
);

// Participants
export const selectParticipants = createSelector(
  selectProcessState,
  (state) => state.participants
);

export const selectParticipantsLoading = createSelector(
  selectProcessState,
  (state) => state.participantsLoading
);

// Risk Matrix
export const selectRiskMatrixState = createSelector(
  selectProcessState,
  (state) => state.riskMatrixState
);

export const selectRiskMatrixLoading = createSelector(
  selectProcessState,
  (state) => state.riskMatrixLoading
);

export const selectRiskMatrixAssessing = createSelector(
  selectProcessState,
  (state) => state.riskMatrixAssessing
);

// Risk Measures
/** US-011: the Compliance Officer's results on their way to the backend. */
export const selectRiskResultsSaving = createSelector(
  selectProcessState,
  (state) => state.riskResultsSaving
);

export const selectRiskMeasures = createSelector(
  selectProcessState,
  (state) => state.riskMeasures
);

export const selectRiskMeasuresLoading = createSelector(
  selectProcessState,
  (state) => state.riskMeasuresLoading
);

export const selectRiskMeasuresSaving = createSelector(
  selectProcessState,
  (state) => state.riskMeasuresSaving
);

// The Compliance Officer's observations (US-005, US-009), by kind: each kind's entry changes only with it.
export const selectProcessObservations = createSelector(selectProcessState, (state) => state.observations);

export const selectAssessResult = createSelector(
  selectProcessState,
  (state) => state.assessResult
);

// Screenings
export const selectScreenings = createSelector(
  selectProcessState,
  (state) => state.screenings
);

export const selectScreeningsLoading = createSelector(
  selectProcessState,
  (state) => state.screeningsLoading
);

export const selectScreeningsRunning = createSelector(
  selectProcessState,
  (state) => state.screeningsRunning
);

// World Check One cases
export const selectWorldcheckCases = createSelector(
  selectProcessState,
  (state) => state.worldcheckCases
);

export const selectWorldcheckCasesLoading = createSelector(
  selectProcessState,
  (state) => state.worldcheckCasesLoading
);

export const selectWorldcheckCasesEmpty = createSelector(
  selectProcessState,
  (state) => state.worldcheckCasesEmpty
);

export const selectWorldcheckCasesError = createSelector(
  selectProcessState,
  (state) => state.worldcheckCasesError
);

/** Where the External Screenings of a process awaiting approval stand (null for any other status).
 *  World-Check comes from the backend's process-level finalStatus: COMPLETED once every person and
 *  company the process covers has a completed report, FAILED when one of them couldn't be screened or
 *  its report failed for good, and null while that is still running. FAILED isn't necessarily final:
 *  the backend re-screens people with no case, and re-runs the country screenings when none were
 *  saved, so it can turn COMPLETED later. World-Check COMPLETED without country results is therefore
 *  FAILED for now, until that re-run saves some.
 *  - LOADING: a list hasn't loaded yet.
 *  - LOAD_ERROR: a list failed to load, so nothing could be checked.
 *  - RUNNING: World-Check still running (or no run yet).
 *  - FAILED: World-Check failed, or the country screenings have no results.
 *  - DONE: World-Check COMPLETED and country results present. */
export type ScreeningsCheck = 'LOADING' | 'LOAD_ERROR' | 'RUNNING' | 'FAILED' | 'DONE';

export const selectScreeningsCheck = createSelector(
  selectProcessState,
  (state): ScreeningsCheck | null => {
    if (state.selectedProcess?.status !== 'FOR_APPROVAL') return null;
    if (state.screeningsError || state.worldcheckCasesError) return 'LOAD_ERROR';
    const worldcheckLoaded = state.worldcheckCasesEmpty || state.worldcheckCases.length > 0;
    if (!state.screeningsLoaded || !worldcheckLoaded) return 'LOADING';
    switch (state.worldcheckFinalStatus) {
      case 'FAILED': return 'FAILED';
      case 'COMPLETED': return state.screenings.length > 0 ? 'DONE' : 'FAILED';
      default: return 'RUNNING';
    }
  }
);

export const selectWorldcheckMissingTargets = createSelector(
  selectProcessState,
  (state) => state.worldcheckMissingTargets
);

/** The screenings section's "in progress" message. */
export const selectScreeningsInProgress = createSelector(
  selectScreeningsCheck,
  (check) => check === 'RUNNING'
);

export const selectProcessStatistics = createSelector(
  selectProcessState,
  (state) => state.statistics
);

export const selectProcessStatisticsLoading = createSelector(
  selectProcessState,
  (state) => state.statisticsLoading
);

// KYC Final Report PDF
export const selectKycReport = createSelector(
  selectProcessState,
  (state) => state.kycReport
);

export const selectKycReportLoading = createSelector(
  selectProcessState,
  (state) => state.kycReportLoading
);

// Full compliance report (US-003)
export const selectComplianceReport = createSelector(
  selectProcessState,
  (state) => state.complianceReport
);

/** A newer full report is awaited: none yet on an answered load (being generated), or one being updated. */
export const selectComplianceReportAwaited = createSelector(
  selectComplianceReport,
  (state) => (state.loaded && !state.report && !state.error) || state.updating
);

// Entrust signature
export const selectSignatureStatus = createSelector(
  selectProcessState,
  (state) => state.signatureStatus
);

export const selectSignatureStatusLoading = createSelector(
  selectProcessState,
  (state) => state.signatureStatusLoading
);

export const selectSignatureStatusLoaded = createSelector(
  selectProcessState,
  (state) => state.signatureStatusLoaded
);

export const selectSignatureStatusFailed = createSelector(
  selectProcessState,
  (state) => state.signatureStatusFailed
);

export const selectEvidenceFolders = createSelector(
  selectProcessState,
  (state) => state.evidenceFolders
);

export const selectEvidenceFoldersLoading = createSelector(
  selectProcessState,
  (state) => state.evidenceFoldersLoading
);

export const selectConsentEvidence = createSelector(selectProcessState, (state) => state.consentEvidence);
export const selectConsentEvidenceLoaded = createSelector(selectProcessState, (state) => state.consentEvidenceLoaded);
export const selectConsentEvidenceHidden = createSelector(selectProcessState, (state) => state.consentEvidenceHidden);
export const selectConsentEvidenceLoadFailed = createSelector(selectProcessState, (state) => state.consentEvidenceLoadFailed);
export const selectConsentEvidenceUploading = createSelector(selectProcessState, (state) => state.consentEvidenceUploading);

export const selectSignatureRequesting = createSelector(
  selectProcessState,
  (state) => state.signatureRequesting
);

export const selectSignatureSkipping = createSelector(
  selectProcessState,
  (state) => state.signatureSkipping
);

/** Entrust Signature statuses that are resolved - no more webhook/polling
 *  updates are coming. Mirrors EntrustSignatureStatus.isTerminal() on the
 *  backend (see EntrustSignatureApplicationService). */
export const SIGNATURE_TERMINAL_STATUSES: NonNullable<EntrustSignatureStatusResponseDto['status']>[] = [
  'APPROVED', 'REVIEW', 'DECLINED', 'ERROR', 'ABANDONED', 'SKIPPED',
];

type SignatureStatus = NonNullable<EntrustSignatureStatusResponseDto['status']>;

/** The signer whose run is the latest: the backend's `currentSigner`, or - from a backend that
 *  predates it (old Client -> Board Member order only) - the board member once their run exists. */
function currentSigner(status: EntrustSignatureStatusResponseDto): 'CLIENT' | 'BOARD_MEMBER' {
  return status.currentSigner ?? (status.boardMember ? 'BOARD_MEMBER' : 'CLIENT');
}

/** The status of the signer whose turn it is: the board member's run (`boardMember.status`) while
 *  they sign first, otherwise the client's (top-level `status`). When the board member has signed
 *  and the backend is still starting the client's run (`clientHandOff` PENDING, US-042) it reads
 *  AWAITING_CLIENT_INPUT, so the UI keeps polling rather than showing their signature as the end;
 *  when that hand-off failed for good (FAILED) it reads ERROR, so staff can request again or skip. */
export const selectSignatureDisplayStatus = createSelector(
  selectSignatureStatus,
  (status): SignatureStatus | null => {
    if (!status) return null;
    if (status.boardMember?.clientHandOff === 'PENDING') return 'AWAITING_CLIENT_INPUT';
    if (status.boardMember?.clientHandOff === 'FAILED') return 'ERROR';
    return currentSigner(status) === 'BOARD_MEMBER'
      ? status.boardMember?.status ?? null
      : status.status ?? null;
  }
);

/** Which signer selectSignatureDisplayStatus is reporting on - lets the UI tell "awaiting the
 *  board member" from "awaiting the client" even though both share the same status vocabulary. */
export const selectSignatureDisplayPhase = createSelector(
  selectSignatureStatus,
  (status): 'CLIENT' | 'BOARD_MEMBER' | null => {
    if (!status) return null;
    return status.boardMember?.clientHandOff ? 'CLIENT' : currentSigner(status);
  }
);

/** True while an existing signature request is still in flight - blocks a
 *  new request client-side (the backend enforces the same rule with a 409)
 *  and drives whether the UI keeps polling for status updates. */
export const selectSignatureNonTerminal = createSelector(
  selectSignatureDisplayStatus,
  (s) => !!s && !SIGNATURE_TERMINAL_STATUSES.includes(s)
);

/**
 * The signature could be skipped now, the permission aside: Compliance approved, nothing in flight, not signed or
 * skipped - the backend's rules on POST …/signature/skip, which also decide when the consent evidence may change
 * (US-015).
 */
export const selectSignatureSkippable = createSelector(
  selectApprovalState,
  selectSignatureStatus,
  selectKycReport,
  selectSignatureNonTerminal,
  selectSignatureRequesting,
  selectSignatureSkipping,
  selectSignatureStatusLoading,
  (approval, status, report, nonTerminal, requesting, skipping, statusLoading) =>
    !!approval?.complianceDone
    && !nonTerminal
    && !requesting
    && !skipping
    && !statusLoading
    && !report?.signed
    && status?.status !== 'SKIPPED'
    && !report?.signatureSkipped
);
